import { Box, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * SectionTitle — h2 with the brand-gradient underline accent. Use to mark
 * the start of a content block inside a polished page (so the eye reads
 * "hero → section → section" rather than a flat wall of cards).
 */
export function SectionTitle({
  title,
  caption,
  action,
  order = 2,
  id,
}: {
  title: ReactNode;
  caption?: ReactNode;
  action?: ReactNode;
  order?: 2 | 3 | 4;
  /**
   * FR-141: passes through to the underlying `<Title>` so a wrapping
   * `<section aria-labelledby="…">` can reference the rendered heading.
   * Without it, callers stamping `aria-labelledby` (artist profile) dangle.
   */
  id?: string;
}) {
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}
    >
      <Stack gap={6} style={{ minWidth: 0 }}>
        <Title
          order={order}
          id={id}
          style={{
            display: 'inline-block',
            paddingBottom: 6,
            position: 'relative',
            margin: 0,
          }}
        >
          {title}
          <Box
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height: 3,
              width: 32,
              background:
                'var(--wwaave-gradient)',
              borderRadius: 2,
            }}
          />
        </Title>
        {caption ? (
          <Text c="ink.6" size="sm">
            {caption}
          </Text>
        ) : null}
      </Stack>
      {action ? (
        <Box
          style={{
            flexShrink: 0,
            fontWeight: 600,
            fontSize: 'var(--mantine-font-size-sm)',
            color: 'var(--mantine-color-brand-7)',
          }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
  );
}
